import React from 'react';
import { formatCurrency } from '../../../../utils/format';
import { formatDate } from '../../../../utils/date';
import { NeoButton, NeoFinancialTable } from '../../../../components/Common';

const PAYMENT_METHOD_LABELS = {
    CREDIT_CARD: "Crédito",
    DEBIT_CARD: "Débito",
    PIX: "Pix",
    CASH: "Dinheiro",
    BOLETO: "Boleto",
    TRANSFER: "Transferência",
};

export const SalesTable = ({ sales, hasMore, loadMore, loading, aggregates, hasSearched }) => {

    // ── Colunas ──
    const salesColumns = [
        {
            key: 'date',
            label: 'Data',
            width: '110px',
            render: (row) => (
                <span className="fw-semibold font-size-12 text-dark">
                    {formatDate(row.createdAt || row.date)}
                </span>
            )
        },
        {
            key: 'saleNumber',
            label: 'Venda',
            width: '130px',
            render: (row) => (
                <div>
                    <span className="fw-bold font-size-12 text-brand-primary d-block">
                        #{row.saleNumber || row.id?.substring(0, 8)}
                    </span>
                    <small className="text-muted font-size-11">
                        {row.items?.length || 0} item(s)
                    </small>
                </div>
            )
        },
        {
            key: 'memberName',
            label: 'Cliente',
            render: (row) => (
                <span className="font-size-12 fw-semibold text-muted">
                    {row.memberName || 'N/A'}
                </span>
            )
        },
        {
            key: 'employeeName',
            label: 'Consultor/Caixa',
            width: '160px',
            render: (row) => (
                <span className="font-size-12 text-muted">
                    {row.employeeName || row.consultantName || 'Não informado'}
                </span>
            )
        },
        {
            key: 'totalAmount',
            label: 'Valor Total',
            width: '120px',
            align: 'right',
            render: (row) => (
                <span className="fw-bold font-size-13 text-success">
                    {formatCurrency(row.totalAmount)}
                </span>
            )
        },
    ];

    // ── Detalhes expandidos ──
    const renderRowDetails = (row) => (
        <div className="row g-3">
            {/* Itens da venda */}
            <div className="col-md-6">
                <p className="text-muted font-size-11 fw-bold text-uppercase mb-2">
                    <i className="mdi mdi-cart-outline me-1"></i>Itens da Venda
                </p>
                {(!row.items || row.items.length === 0) ? (
                    <small className="text-muted">Nenhum item detalhado.</small>
                ) : (
                    <div className="d-flex flex-column gap-1">
                        {row.items.map((item, idx) => (
                            <div key={idx} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                <span className="font-size-12">
                                    {item.quantity || 1}x {item.name || item.membershipName || 'Item'}
                                </span>
                                <span className="fw-semibold font-size-12">
                                    {formatCurrency(item.totalPrice || (item.unitPrice * (item.quantity || 1)) || 0)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Formas de pagamento */}
            <div className="col-md-6">
                <p className="text-muted font-size-11 fw-bold text-uppercase mb-2">
                    <i className="mdi mdi-cash-multiple me-1"></i>Formas de Pagamento
                </p>
                {(!(row.payments || row.paymentMethods) || (row.payments || row.paymentMethods || []).length === 0) ? (
                    <small className="text-muted">Nenhum pagamento detalhado.</small>
                ) : (
                    <div className="d-flex flex-column gap-1">
                        {(row.payments || row.paymentMethods || []).map((pm, idx) => (
                            <div key={idx} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                <span className="font-size-12">
                                    {pm.methodLabel || PAYMENT_METHOD_LABELS[pm.methodId] || PAYMENT_METHOD_LABELS[pm.idMethod] || pm.methodId || '—'}
                                    {(pm.installments || 1) > 1 && ` (${pm.installments}x)`}
                                </span>
                                <span className="fw-semibold font-size-12">
                                    {formatCurrency(pm.value || pm.amount || 0)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Totais */}
            {(row.discountTotal > 0 || row.subtotal > 0) && (
                <div className="col-12 border-top pt-2 d-flex justify-content-end gap-4">
                    {row.discountTotal > 0 && (
                        <span className="text-muted font-size-12">
                            Desconto: <strong>{formatCurrency(row.discountTotal)}</strong>
                        </span>
                    )}
                    <span className="text-muted font-size-12">
                        Subtotal: <strong>{formatCurrency(row.subtotal || 0)}</strong>
                    </span>
                </div>
            )}
        </div>
    );

    // ── Summary ──
    const salesSummary = aggregates && !aggregates.loading ? [
        {
            label: 'Total de Vendas',
            value: String(aggregates.count || 0),
            icon: 'mdi-cart-check',
            statusClass: 'status-paid'
        },
        {
            label: 'Valor Total',
            value: formatCurrency(aggregates.totalAmount || 0),
            icon: 'mdi-cash-multiple',
            statusClass: 'status-paid'
        },
    ] : [];

    const emptyMessage = !hasSearched ? "Pronto para buscar" : "Nenhuma venda encontrada";
    const emptySubMessage = !hasSearched
        ? "Selecione o período acima e clique em 'Buscar' para visualizar as vendas."
        : "Ajuste o período e busque novamente.";

    return (
        <>
            <NeoFinancialTable
                columns={salesColumns}
                data={sales || []}
                keyField="id"
                loading={loading}
                summaryData={salesSummary}
                renderRowDetails={renderRowDetails}
                defaultItemsPerPage={20}
                itemsPerPageOptions={[10, 20, 50]}
                emptyMessage={emptyMessage}
                emptySubMessage={emptySubMessage}
            />

            {/* Load More (Firebase pagination) */}
            {hasMore && (
                <div className="text-center py-3 border-top">
                    <NeoButton
                        variant="outline-secondary"
                        size="sm"
                        onClick={loadMore}
                        loading={loading}
                        loadingText="Carregando..."
                        className="px-4"
                    >
                        Carregar mais
                    </NeoButton>
                </div>
            )}
        </>
    );
};

export default SalesTable;
